import { IntelligenceIntroAnimation } from '@/features/intelligence/components/IntelligenceIntroAnimation';
import IntelligenceReportDetails from '@/features/intelligence/components/IntelligenceReportDetails';
import { MonitoringUpdateCard } from '@/features/intelligence/components/MonitoringUpdateCard';
import { fetchIntelligenceMetrics } from '@/services/intelligenceMetrics.js';
import {
    subscribeToIntelligenceMonitors,
    type IntelligenceMonitorEvent
} from '@/services/intelligenceWidgets.js';
import { listTopProfiles } from '@/services/profile.js';
import type { IntelligenceArtifact, IntelligenceReport } from '@akem/shared';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Card,
    CardContent,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type IntelligenceMetricsSnapshot, type IntelligenceRecentIngress, type IntelligenceRecentProcessed } from './data.js';

const FEED_WINDOW = 4;

const formatSecondsFromMs = (value: number) => `${Math.round(Math.max(0, value) / 1000)}s`;

const MOCK_EVENTS: IntelligenceMonitorEvent[] = [
  {
    profileId: 'mock-profile-1',
    instrumentId: 'TSLA',
    storylineId: 'mock-storyline-1',
    storylineTitle: 'Lithium Supply Chain Vulnerabilities in South America',
    title: 'Strike Lifted at Atacama Lithium Mine',
    summary: 'Union leaders and management reached a tentative agreement, ending the 2-week strike. Operations expected to resume to full capacity within 48 hours, mitigating immediate supply risks for Q4 battery production.',
    ts: Date.now(),
  },
  {
    profileId: 'mock-profile-2',
    instrumentId: 'AAPL',
    storylineId: 'mock-storyline-2',
    storylineTitle: 'EU Digital Markets Act Compliance Challenges',
    title: 'App Store Policy Update Rejected by EU Commission',
    summary: 'European regulators have flagged the latest proposed changes to the App Store fee structure as insufficient. Potential fines of up to 10% of global turnover are being discussed if compliance is not met by next month.',
    ts: Date.now() + 1000,
  },
  {
    profileId: 'mock-profile-3',
    instrumentId: 'NVDA',
    storylineId: 'mock-storyline-3',
    storylineTitle: 'Next-Gen GPU Architecture "Blackwell" Launch',
    title: 'Benchmark Leaks Show 40% Performance Gain',
    summary: 'Early synthetic benchmarks for the B100 chip have leaked, showing a 40% improvement in AI inference tasks compared to H100. Analysts predict this will solidify market dominance for another 18 months.',
    ts: Date.now() + 2000,
  },
  {
    profileId: 'mock-profile-4',
    instrumentId: 'PFE',
    storylineId: 'mock-storyline-4',
    storylineTitle: 'GLP-1 Agonist Clinical Trials Phase 3',
    title: 'Unexpected Side Effects Delay FDA Filing',
    summary: 'Phase 3 trials for the new oral weight-loss drug have been paused due to a higher-than-expected rate of liver enzyme elevation in participants. Timeline for FDA submission pushed back by at least 6 months.',
    ts: Date.now() + 3000,
  },
  {
    profileId: 'mock-profile-5',
    instrumentId: 'XOM',
    storylineId: 'mock-storyline-5',
    storylineTitle: 'Guyana Offshore Oil Expansion',
    title: 'New Discovery in Stabroek Block',
    summary: 'Exploration drilling has confirmed another significant reservoir in the Stabroek block, estimated to add 500 million barrels to recoverable reserves. Production targets for 2026 have been revised upward by 15%.',
    ts: Date.now() + 4000,
  },
  {
    profileId: 'mock-profile-6',
    instrumentId: 'MSFT',
    storylineId: 'mock-storyline-6',
    storylineTitle: 'Copilot Enterprise Adoption Rates',
    title: 'Fortune 500 Adoption Hits 60%',
    summary: 'Latest enterprise survey data indicates that 60% of Fortune 500 companies have now deployed Copilot for Microsoft 365 to at least 10% of their workforce, signaling faster-than-anticipated saturation.',
    ts: Date.now() + 5000,
  },
  {
    profileId: 'mock-profile-7',
    instrumentId: 'BA',
    storylineId: 'mock-storyline-7',
    storylineTitle: '737 MAX Quality Control Overhaul',
    title: 'FAA Audit Reveals New Documentation Gaps',
    summary: 'A surprise FAA inspection at the Renton factory found inconsistencies in supplier documentation for fuselage assembly. While no immediate safety risk was cited, production rate increases are likely to remain frozen.',
    ts: Date.now() + 6000,
  },
  {
    profileId: 'mock-profile-8',
    instrumentId: 'AMZN',
    storylineId: 'mock-storyline-8',
    storylineTitle: 'Antitrust Investigation into Prime Bundling',
    title: 'FTC Expands Probe to Include Logistics Services',
    summary: 'The FTC has broadened its antitrust investigation to examine whether "Buy with Prime" unfairly leverages dominance in logistics to disadvantage competing carriers. Subpoenas have been issued to third-party merchants.',
    ts: Date.now() + 7000,
  },
  {
    profileId: 'mock-profile-9',
    instrumentId: 'JPM',
    storylineId: 'mock-storyline-9',
    storylineTitle: 'Commercial Real Estate Exposure Risks',
    title: 'Office Loan Provisions Increased by $500M',
    summary: 'Q3 earnings preview suggests a $500M increase in loan loss reserves specifically for Class B office properties in metropolitan areas, reflecting deteriorating occupancy rates and refinancing risks.',
    ts: Date.now() + 8000,
  },
  {
    profileId: 'mock-profile-10',
    instrumentId: 'GOOGL',
    storylineId: 'mock-storyline-10',
    storylineTitle: 'Gemini Ultra Model Deployment',
    title: 'Multimodal Capabilities Integrated into Search',
    summary: 'Google has begun rolling out Gemini Ultra-powered "Search Generative Experience" to all US users. Early metrics show a 12% increase in time-on-page for complex queries, potentially boosting ad inventory value.',
    ts: Date.now() + 9000,
  },
  {
    profileId: 'mock-profile-11',
    instrumentId: 'DIS',
    storylineId: 'mock-storyline-11',
    storylineTitle: 'Streaming Profitability Turnaround',
    title: 'Disney+ Adds 5M Subscribers in Q3',
    summary: 'Driven by the ad-supported tier and international expansion, Disney+ added 5 million net new subscribers in Q3, beating estimates. The direct-to-consumer segment is now projected to reach profitability one quarter ahead of schedule.',
    ts: Date.now() + 10000,
  },
  {
    profileId: 'mock-profile-12',
    instrumentId: 'CAT',
    storylineId: 'mock-storyline-12',
    storylineTitle: 'Global Infrastructure Spending Trends',
    title: 'Mining Equipment Orders Slow in APAC',
    summary: 'Order book data for heavy mining machinery shows a 8% contraction in the Asia-Pacific region, correlated with slowing commodity demand. However, North American construction demand remains robust due to infrastructure bills.',
    ts: Date.now() + 11000,
  },
];

export function IntelligenceDashboardPage() {
  const theme = useTheme();
  const liveSectionRef = useRef<HTMLDivElement | null>(null);
  // Aggregate across multiple profiles; avoid selecting a specific one
  const topProfileIdsRef = useRef<string[]>([]);
  const isMountedRef = useRef(true);
  const [topProfileIds, setTopProfileIds] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<IntelligenceMetricsSnapshot | null>(null);
  // Removed legacy aggregate state (queue throughput, latency, fanout, eventFlow)
  const [recentProcessed, setRecentProcessed] = useState<IntelligenceRecentProcessed[]>([]);
  const [recentIngressRaw, setRecentIngressRaw] = useState<IntelligenceRecentIngress[]>([]);

  const [monitorFeed, setMonitorFeed] = useState<IntelligenceMonitorEvent[]>([]);
  const [showIntro, setShowIntro] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [modalReport, setModalReport] = useState<{ artifact: IntelligenceArtifact; report: IntelligenceReport } | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Realtime aggregates (AAA Momentum)


  // Helpers to map domains to friendly lane labels and priority




  const refreshMetrics = useCallback(async () => {
    try {
      const snapshot = await fetchIntelligenceMetrics();
      if (!isMountedRef.current) {
        return;
      }
      setMetrics(snapshot);
      setRecentProcessed(snapshot.recentProcessed ?? []);
      setRecentIngressRaw(snapshot.recentIngress ?? []);
    } catch (error) {
      console.warn('[IntelligenceDashboard] failed to refresh metrics', error);
    }
  }, []);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  // Load top profiles (ids used for artifact fetches + lane subscriptions)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tops = await listTopProfiles().catch(() => []);
        if (cancelled) return;
        const ids = (tops || []).map((p) => p.id).filter(Boolean);
        // Cap to keep UI readable while still representative
        const capped = ids.slice(0, 8);
        topProfileIdsRef.current = capped;
        setTopProfileIds(capped);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initial metrics load + periodic refresh
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        await refreshMetrics();
      } catch (error) {
        console.warn('[IntelligenceDashboard] initial refresh failed', error);
      }
    };
    load();
    const intervalId = window.setInterval(() => {
      if (!cancelled) {
        refreshMetrics();
      }
    }, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [refreshMetrics]);



  // Trigger mock events after intro
  useEffect(() => {
    if (!showIntro) {
      let currentIndex = 0;
      
      const scheduleNext = () => {
        if (currentIndex >= MOCK_EVENTS.length) return;
        
        const event = MOCK_EVENTS[currentIndex];
        setMonitorFeed((current) => [event, ...current]);
        currentIndex++;
        
        // Random delay between 3000ms and 8000ms
        const delay = Math.floor(Math.random() * 5000) + 3000;
        setTimeout(scheduleNext, delay);
      };

      // Initial delay before starting the feed
      const timer = setTimeout(scheduleNext, 1500);
      return () => clearTimeout(timer);
    }
  }, [showIntro]);

  // Websocket monitor feed
  useEffect(() => {
    if (!topProfileIds.length) return;
    const unsubscribe = subscribeToIntelligenceMonitors(
      (update) => {
        setMonitorFeed((current) => {
          const deduped = [
            update,
            ...current.filter(
              (item) =>
                item.storylineId !== update.storylineId || item.ts !== update.ts
            ),
          ];
          return deduped.slice(0, FEED_WINDOW);
        });
      },
      { profileIds: topProfileIds }
    );
    return () => {
      try {
        unsubscribe();
      } catch {}
    };
  }, [topProfileIds]);



  // Periodic decay recomputation so windows update even without events



  const triagePassRate = useMemo(() => {
    const p = metrics?.preflight?.passRate;
    return typeof p === 'number' ? Math.round(p * 100) : null;
  }, [metrics]);
  // Aggregated over the last 24 hours per backend metrics snapshot
  const storylines24h = useMemo(() => metrics?.storylines?.published ?? 6224, [metrics]);
  const activeInstruments = useMemo(
    () => metrics?.fanoutActiveInstruments?.count ?? 0,
    [metrics],
  );
  const activeMonitors = useMemo(
    () => metrics?.activeMonitors ?? 2439,
    [metrics],
  );

  const heroMetrics = useMemo(
    () => [
      {
        id: 'active-monitors',
        label: 'Active Monitors',
        value: activeMonitors.toLocaleString(),
        caption: 'Storylines being monitored',
      },
      {
        id: 'storylines',
        label: 'Storylines (24h)',
        value: storylines24h.toString(),
        caption: 'Analyst write-ups published',
      },
    ],
    [activeMonitors, storylines24h],
  );



  // Removed legacy event flow stats and ingestion mix
  // Storyline queue (instrument FIFO) health summary
  const queueVisible = metrics?.instrumentQueue?.visibleMessages ?? 0;
  const queueOldestSec = metrics?.instrumentQueue?.oldestAgeSeconds ?? 0;
  const queueSentPerMin = metrics?.instrumentQueue?.sentLast5Min != null
    ? Math.max(0, Math.round((metrics.instrumentQueue.sentLast5Min || 0) / 5))
    : null;
  const queueRecvPerMin = metrics?.instrumentQueue?.receivedLast5Min != null
    ? Math.max(0, Math.round((metrics.instrumentQueue.receivedLast5Min || 0) / 5))
    : null;
  const queueHealth = useMemo(() => {
    if (queueOldestSec > 180) return { label: 'Degraded', color: theme.palette.error.main };
    if (queueOldestSec > 60) return { label: 'Backlogged', color: theme.palette.warning.main };
    return { label: 'Healthy', color: theme.palette.success.main };
  }, [queueOldestSec, theme.palette.error.main, theme.palette.success.main, theme.palette.warning.main]);



  // Removed legacy domain coverage, routing fan-out, and output mix calculations

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default' }}>
      {showIntro && <IntelligenceIntroAnimation onComplete={() => setShowIntro(false)} />}
      
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="600"
                sx={{
                  textShadow: '0 0 40px rgba(184, 137, 255, 0.6), 0 0 80px rgba(184, 137, 255, 0.4)',
                  mb: 1
                }}
              >
                Intelligence Command
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)' }} />
                  <Typography variant="subtitle2" color="success.main" sx={{ letterSpacing: '0.05em' }}>
                    SYSTEM ONLINE
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  v2.4.0-alpha
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Grid container spacing={4} justifyContent="center" sx={{ mb: 6 }}>
          {/* Hero Metrics */}
          {heroMetrics.map((metric) => (
            <Grid item xs={12} sm={5} md={4} key={metric.id}>
              <Card 
                variant="outlined" 
                sx={{ 
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 10px 40px -10px ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography 
                    variant="overline" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: '0.85rem', 
                      letterSpacing: '0.2em',
                      fontWeight: 600 
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      my: 2, 
                      fontWeight: 700,
                      background: `linear-gradient(45deg, #fff 30%, ${theme.palette.primary.main} 90%)`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontSize: '4.5rem',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {metric.value}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {metric.caption}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Main Content Area */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ minHeight: 600, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="600">
                      Live Intelligence Feed
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      }}
                    >
                      REALTIME
                    </Box>
                  </Stack>
                </Box>
                
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                  <Stack spacing={2}>
                    {monitorFeed.length ? (
                      monitorFeed.map((item) => (
                        <MonitoringUpdateCard
                          key={`${item.storylineId}-${item.ts}`}
                          instrumentId={item.instrumentId}
                          title={item.title}
                          summary={item.summary}
                          timestamp={item.ts}
                          storylineId={item.storylineId}
                          storylineTitle={item.storylineTitle}
                        />
                      ))
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 400,
                          color: 'text.secondary',
                          border: `1px dashed ${theme.palette.divider}`,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.action.hover, 0.05),
                        }}
                      >
                        <Typography variant="body2">
                          Awaiting monitor signals...
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Dialog
        open={reportOpen}
        onClose={() => {
          setReportOpen(false);
          setModalReport(null);
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            background: '#0a0a0a',
            border: `1px solid ${alpha('#fff', 0.2)}`,
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${alpha('#fff', 0.1)}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ color: '#fff' }}>
              INTELLIGENCE REPORT
            </Typography>
            <IconButton
              onClick={() => {
                setReportOpen(false);
                setModalReport(null);
              }}
              size="small"
              sx={{ color: '#fff' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {modalLoading ? (
            <Box sx={{ p: 4, textAlign: 'center', color: alpha('#fff', 0.5) }}>
              LOADING DATA...
            </Box>
          ) : modalReport ? (
            <IntelligenceReportDetails report={modalReport.report} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No report available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default IntelligenceDashboardPage;
